import React from 'react';
import {
    User, Bell, Calendar, LifeBuoy,
    ChevronRight, Clock, CheckCircle, AlertCircle,
    MessageSquare,
    ExternalLink,
    Building2Icon
} from 'lucide-react';
import { useGetTeamMemberDashboardStats } from '../../custom-hooks/use-stats';
import { convertToPresentableText } from '../../utils/functions';
import moment from 'moment';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Spinner, Dialog, DialogHeader, DialogBody, DialogFooter } from '@material-tailwind/react';
import { useAuth } from '../../services/contexts/authContext';
import { URLS } from '../../router-x/routes';
import AnnouncementModal from '../customer.home/AnnouncementModal';
import PortalHomeHeader from '../../components/portal-home-header';

const QuickLink = ({ to, icon, title, subtitle }) => (
    <RouterLink
        to={to}
        className="flex items-center gap-3.5 rounded-2xl border border-[#ECE3D5] bg-white p-[18px] transition-all hover:-translate-y-0.5 hover:shadow-[0_16px_34px_-20px_rgba(40,22,8,0.4)]"
    >
        <div className="flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-xl bg-cream-input text-brand">
            {icon}
        </div>
        <div className="min-w-0 flex-1">
            <div className="font-display text-[15px] font-semibold text-ink">{title}</div>
            <div className="mt-0.5 text-[12.5px] text-[#8C8175]">{subtitle}</div>
        </div>
        <ExternalLink size={18} className="shrink-0 text-[#C5B9AA]" />
    </RouterLink>
);

const TeamMemberDashboard = () => {

    const { user, userDetails } = useAuth();

    const { data: stats, isLoading, error } = useGetTeamMemberDashboardStats();

    const [showAllBookings, setShowAllBookings] = React.useState(false);
    const [showAllNotifications, setShowAllNotifications] = React.useState(false);
    const [showSupportModal, setShowSupportModal] = React.useState(false);
    const navigate = useNavigate();

    const handleSupportClick = () => {
        navigate(URLS.TEAM_MEMBER.SUPPORT);
    };

    if (isLoading) {
        return <div className='flex min-h-[60vh] items-center justify-center'>
            <Spinner className="h-8 w-8" color="orange" />
        </div>;
    }

    if (error) {
        return <div className="px-6 py-10 text-center text-[#8C8175]">Error: {error.message}</div>;
    }

    if (stats && stats.length === 0) {
        return <div className="px-6 py-10 text-center text-[#8C8175]">No stats found</div>;
    }

    const quotaUsed = stats.meetingQuota?.used;
    const quotaTotal = stats.meetingQuota?.total;
    const remainingPct = quotaTotal ? parseInt(100 - ((quotaUsed / quotaTotal) * 100)) : 0;
    const unreadCount = stats?.notifications?.filter(n => !n.read).length || 0;

    // Every user record (customer or team member) has a non-zero meetingRoomQuota by
    // default (schema default, not an "unset" state) — so a personal cap only means
    // something distinct once it no longer matches the org's own monthly total. Most
    // team members will match (no real restriction); this card only shows when the
    // owner has actually changed it away from that.
    const personalMonthlyMin = userDetails?.meetingRoomQuota?.monthlyHours || 0;
    const personalUsedMin = userDetails?.meetingRoomQuota?.usedHours || 0;
    const personalRemainingPct = personalMonthlyMin ? parseInt(100 - ((personalUsedMin / personalMonthlyMin) * 100)) : 0;
    const hasCustomPersonalCap = personalMonthlyMin !== (quotaTotal || 0);

    return (
        <div className="animate-fadeIn px-4 py-5 md:px-[34px] md:py-[30px]">
                <PortalHomeHeader name={convertToPresentableText(user?.displayName || '')} />

                {/* Quick Links */}
                <div className="mb-[18px] grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    <QuickLink to={URLS.TEAM_MEMBER.PROFILE} icon={<User size={24} />} title="Profile" subtitle="Manage your account" />
                    <QuickLink to={URLS.TEAM_MEMBER.MEETING_ROOM} icon={<Calendar size={24} />} title="Meeting Rooms" subtitle="Book a room" />
                    {userDetails?.allowedPages?.includes('support') && (
                        <QuickLink to={URLS.TEAM_MEMBER.SUPPORT} icon={<LifeBuoy size={24} />} title="Support" subtitle="Contact us" />
                    )}
                </div>

                {/* Parent Organization Banner */}
                <div className="mb-[18px] flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#ECE3D5] bg-white p-5">
                    <div className="flex items-center gap-3">
                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-cream-input text-brand">
                            <Building2Icon size={22} />
                        </div>
                        <div>
                            <h3 className="font-display text-base font-semibold text-ink">{convertToPresentableText(stats.customerId?.name)}</h3>
                            <p className="text-sm text-[#8C8175]">Parent Customer Organization</p>
                        </div>
                    </div>
                    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${stats.customerId?.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {stats.customerId?.isActive ? 'Active' : 'Inactive'}
                    </span>
                </div>

                {/* Meeting Quota Card */}
                <div className="mb-[18px] rounded-2xl border border-[#ECE3D5] bg-white p-6">
                    <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                        <div className="flex items-center gap-4">
                            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-cream-input text-brand">
                                <Clock size={24} />
                            </div>
                            <div>
                                <h3 className="font-display text-base font-semibold text-ink">Organization's Meeting Room Quota</h3>
                                <p className="text-sm text-[#8C8175]">{stats.meetingQuota?.remaining / 60} of {stats.meetingQuota?.total / 60} hours remaining this month</p>
                            </div>
                        </div>

                        <RouterLink
                            to={URLS.TEAM_MEMBER.MEETING_ROOM}
                            className="inline-flex w-full items-center justify-center rounded-xl bg-brand px-6 py-2.5 font-display text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-brand-dark hover:shadow-[0_12px_24px_-12px_rgba(190,83,16,0.8)] md:w-auto"
                        >
                            Book a Room
                        </RouterLink>
                    </div>

                    {/* Personal Limit bar — only shown when the org owner has set a cap different from the org total */}
                    {hasCustomPersonalCap && (
                        <div className="mt-5">
                            <div className="mb-1.5 flex flex-wrap items-center justify-between gap-1">
                                <span className="text-xs font-semibold uppercase tracking-wide text-[#8C8175]">Personal Limit</span>
                                <span className="text-xs text-[#8C8175]">
                                    {(personalUsedMin / 60).toFixed(1)} of {(personalMonthlyMin / 60).toFixed(1)} hours used this month
                                </span>
                            </div>
                            <div className="h-2.5 w-full overflow-hidden rounded-full bg-cream-100">
                                <div className="h-full rounded-full bg-brand transition-all" style={{ width: `${personalRemainingPct}%` }} />
                            </div>
                            <div className="mt-2 flex flex-wrap items-center justify-between gap-1">
                                <p className="text-xs text-[#8C8175]">Set for you by your organization owner, drawn from the org quota below</p>
                                <span className="text-xs font-semibold text-brand">{personalRemainingPct}% remaining</span>
                            </div>
                        </div>
                    )}

                    <div className="mt-5">
                        {hasCustomPersonalCap && (
                            <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-[#8C8175]">Organization's Total Quota</span>
                        )}
                        <div className="h-2.5 w-full overflow-hidden rounded-full bg-cream-100">
                            <div className="h-full rounded-full bg-brand transition-all" style={{ width: `${remainingPct}%` }} />
                        </div>
                        <div className="mt-2 flex flex-wrap items-center justify-between gap-1">
                            <p className="text-xs text-[#8C8175]">
                                Quota resets on the 1st of each month
                                {' '}· Last reset: {stats.meetingQuota?.lastResetDate ? moment(stats.meetingQuota?.lastResetDate).format('DD MMM YYYY') : 'Never'}
                            </p>
                            <span className="text-xs font-semibold text-brand">{remainingPct}% remaining</span>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                    {/* Upcoming Bookings */}
                    <section className="rounded-2xl border border-[#ECE3D5] bg-white p-6">
                        <div className="mb-4 flex items-center justify-between">
                            <h2 className="font-display text-base font-semibold text-ink">Upcoming Bookings</h2>
                            <RouterLink to={URLS.TEAM_MEMBER.MEETING_ROOM} className="flex items-center gap-1 text-sm font-semibold text-brand hover:underline">
                                View all <ChevronRight className="h-4 w-4" />
                            </RouterLink>
                        </div>
                        {stats.upcomingMeetingRoomBookings?.length > 0 ? (
                            <div className="space-y-4">
                                {stats.upcomingMeetingRoomBookings.slice(0, showAllBookings ? undefined : 3).map(booking => {
                                    return (
                                        <div key={booking._id} className="rounded-xl border border-[#ECE3D5] p-4 transition-colors hover:bg-cream">
                                            <div className="flex items-start gap-3">
                                                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-cream-input text-brand">
                                                    <Clock className="h-5 w-5" />
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <h3 className="font-medium text-ink">{booking.meetingRoom?.name}</h3>
                                                    <p className="text-sm text-[#8C8175]">{booking.meetingRoom?.location}</p>
                                                    <div className="mt-1.5 flex items-center gap-2 text-sm text-[#8C8175]">
                                                        <span>{moment(booking.startDate).format('DD MMM YYYY')}</span>
                                                        <span>•</span>
                                                        <span>{moment(booking.startDate).format('h:mm A')} - {moment(booking.endDate).format('h:mm A')}</span>
                                                    </div>
                                                </div>
                                                <RouterLink
                                                    to={`${URLS.TEAM_MEMBER.MEETING_ROOM}/booking/${booking._id}`}
                                                    className="shrink-0 rounded-lg bg-cream-100 px-2.5 py-1 text-xs font-medium text-[#6B675F] transition-colors hover:bg-cream-input"
                                                >
                                                    Details
                                                </RouterLink>
                                            </div>
                                        </div>
                                    )
                                })}
                                {stats.upcomingMeetingRoomBookings.length > 3 && (
                                    <div className="text-center">
                                        <button
                                            onClick={() => setShowAllBookings(!showAllBookings)}
                                            className="text-sm font-semibold text-brand hover:underline"
                                        >
                                            {showAllBookings ? 'Show Less' : `Show ${stats.upcomingMeetingRoomBookings.length - 3} More`}
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="py-8 text-center">
                                <Clock className="mx-auto mb-3 h-12 w-12 text-[#D8CDBD]" />
                                <p className="text-[#8C8175]">No upcoming bookings</p>
                                <RouterLink
                                    to={URLS.TEAM_MEMBER.MEETING_ROOM}
                                    className="mt-2 inline-block text-sm font-semibold text-brand hover:underline"
                                >
                                    Book a meeting room
                                </RouterLink>
                            </div>
                        )}
                    </section>

                    {/* Notifications */}
                    <section className="rounded-2xl border border-[#ECE3D5] bg-white p-6">
                        <div className="mb-4 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <h2 className="font-display text-base font-semibold text-ink">Notifications</h2>
                                <span className="rounded-full bg-cream-input px-2.5 py-0.5 text-xs font-semibold text-brand">
                                    {(stats?.notifications?.length > 0 && unreadCount > 0) ? `${unreadCount} new` : 'No new notifications'}
                                </span>
                            </div>
                            <RouterLink
                                to={URLS.TEAM_MEMBER.NOTIFICATIONS}
                                className="flex items-center gap-1 text-sm font-semibold text-brand hover:underline"
                            >
                                View all <ChevronRight className="h-4 w-4" />
                            </RouterLink>
                        </div>

                        {stats.notifications?.length > 0 ? (
                            <div className="space-y-3">
                                {stats.notifications.slice(0, showAllNotifications ? undefined : 3).map(notification => (
                                    <div
                                        key={notification.id}
                                        className={`rounded-xl p-3 ${notification.read ? 'bg-cream' : 'border-l-4 border-brand bg-cream-input/50'}`}
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${notification.type === 'info' ? 'bg-cream-input text-brand' :
                                                notification.type === 'alert' ? 'bg-amber-100 text-amber-600' : 'bg-green-100 text-green-600'
                                                }`}>
                                                {notification.type === 'info' ? (
                                                    <Bell className="h-4 w-4" />
                                                ) : notification.type === 'alert' ? (
                                                    <AlertCircle className="h-4 w-4" />
                                                ) : (
                                                    <CheckCircle className="h-4 w-4" />
                                                )}
                                            </div>
                                            <div>
                                                <p className="text-sm text-ink">{notification.message}</p>
                                                <span className="text-xs text-[#8C8175]">{notification.date}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {stats.notifications.length > 3 && (
                                    <div className="pt-1 text-center">
                                        <button
                                            onClick={() => setShowAllNotifications(!showAllNotifications)}
                                            className="text-sm font-semibold text-brand hover:underline"
                                        >
                                            {showAllNotifications ? 'Show Less' : `Show ${stats.notifications.length - 3} More`}
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="py-8 text-center">
                                <Bell className="mx-auto mb-3 h-12 w-12 text-[#D8CDBD]" />
                                <p className="text-[#8C8175]">No notifications</p>
                            </div>
                        )}
                    </section>
                </div>

                {/* Support Section */}
                {userDetails?.allowedPages?.includes('support') && (
                    <section className="mt-[18px] rounded-2xl border border-[#ECE3D5] bg-white p-6">
                        <h2 className="mb-4 font-display text-base font-semibold text-ink">Need Help?</h2>

                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div className="rounded-xl border border-[#ECE3D5] p-4 text-center">
                                <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-cream-input text-brand">
                                    <MessageSquare className="h-5 w-5" />
                                </div>
                                <h3 className="font-medium text-ink">Chat Support</h3>
                                <p className="mb-3 text-sm text-[#8C8175]">Available 24/7 for your queries</p>
                                <div
                                    onClick={handleSupportClick}
                                    className="block w-full cursor-pointer rounded-full bg-brand px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-dark"
                                >
                                    Start Chat
                                </div>
                            </div>

                            <div className="rounded-xl border border-[#ECE3D5] p-4 text-center">
                                <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-cream-input text-brand">
                                    <LifeBuoy className="h-5 w-5" />
                                </div>
                                <h3 className="font-medium text-ink">Help Center</h3>
                                <p className="mb-3 text-sm text-[#8C8175]">Browse FAQs and tutorials</p>
                                <div
                                    onClick={handleSupportClick}
                                    className="block w-full cursor-pointer rounded-full border border-brand bg-white px-4 py-2 text-sm font-semibold text-brand transition-colors hover:bg-cream-input"
                                >
                                    Visit Help Center
                                </div>
                            </div>
                        </div>
                    </section>
                )}

            {/* Access Denied Modal */}
            <Dialog open={showSupportModal} handler={() => setShowSupportModal(false)} className="rounded-2xl">
                <DialogHeader className="font-display text-ink">Access Denied</DialogHeader>
                <DialogBody className="text-[#6B675F]">
                    You do not have access to the support portal. Please contact your supervisor ({stats?.customerId?.name ? convertToPresentableText(stats.customerId.name) : 'account administrator'}) to request access.
                </DialogBody>
                <DialogFooter>
                    <button
                        onClick={() => setShowSupportModal(false)}
                        className="rounded-xl bg-brand px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-dark"
                    >
                        Close
                    </button>
                </DialogFooter>
            </Dialog>

            {/* 1-Time Announcement Modal */}
            <AnnouncementModal />
        </div>
    );
};

export default TeamMemberDashboard;
